import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Settings as SettingsIcon, User, Smartphone, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLE_LABELS = { super_admin: 'Super Admin', admin: 'Admin', agent: 'Agent' };

export default function SettingsPage() {
  const { user, checkAuth } = useAuth();
  const [setupStep, setSetupStep] = useState(null); // null | 'qr' | 'verify'
  const [qrData, setQrData] = useState(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  const handleSetup2FA = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/setup-2fa`, { withCredentials: true });
      setQrData(res.data);
      setSetupStep('qr');
    } catch (e) { toast.error('Failed to start 2FA setup'); }
    finally { setLoading(false); }
  };

  const handleConfirm2FA = async () => {
    if (!code || code.length !== 6) { toast.error('Enter a 6-digit code'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/confirm-2fa`, { code }, { withCredentials: true });
      toast.success('2FA enabled successfully!');
      setSetupStep(null);
      setCode('');
      checkAuth();
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setLoading(false); }
  };

  const handleDisable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) { toast.error('Enter your 2FA code'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/disable-2fa`, { code: disableCode }, { withCredentials: true });
      toast.success('2FA disabled');
      setDisableCode('');
      checkAuth();
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex-1 p-8" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>Settings</h1>
        <p className="text-[#B0B0B0] text-sm mt-1">Manage your account and security</p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Profile */}
        <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-6">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <User size={18} className="text-[#00FF99]" /> Profile
          </h2>
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[#1A1A1A] text-[#00FF99] text-lg">{user?.name?.charAt(0) || 'U'}</AvatarFallback>
            </Avatar>
            <div>
              <p className="text-white font-bold text-lg">{user?.name || 'User'}</p>
              <p className="text-[#B0B0B0] text-sm">{user?.email || ''}</p>
              <p className="text-xs text-[#808080] mt-1">Role: {ROLE_LABELS[user?.role] || user?.role}</p>
            </div>
          </div>
        </div>

        {/* 2FA Setup */}
        <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-6">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Smartphone size={18} className="text-[#00FF99]" /> Two-Factor Authentication
          </h2>

          <div className="flex items-center gap-2 mb-4 text-[#00FF99]">
            <CheckCircle size={18} />
            <span className="text-sm font-medium">
              {user?.totp_enabled ? 'Authenticator app is active' : 'Email login codes are active'}
            </span>
          </div>

          {!user?.totp_enabled && !setupStep && (
            <div>
              <p className="text-sm text-[#B0B0B0] mb-3">
                Every sign-in already requires a 6-digit code sent to your email. For even stronger
                security you can switch to an authenticator app (Google Authenticator, Authy, etc.).
              </p>
              <Button onClick={handleSetup2FA} disabled={loading} data-testid="setup-2fa-btn"
                className="bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
                <Smartphone size={16} className="mr-2" /> Use Authenticator App
              </Button>
            </div>
          )}

          {setupStep === 'qr' && qrData && (
            <div className="space-y-4">
              <p className="text-sm text-[#B0B0B0]">
                1. Scan this QR code with your authenticator app:
              </p>
              <div className="bg-white rounded-lg p-4 inline-block">
                <img src={qrData.qr_code} alt="QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
              </div>
              <div>
                <p className="text-xs text-[#808080] mb-1">Or enter this secret manually:</p>
                <code className="text-xs text-[#00FF99] bg-[#0A0A0A] px-3 py-1 rounded font-mono">{qrData.secret}</code>
              </div>
              <div>
                <p className="text-sm text-[#B0B0B0] mb-2">2. Enter the 6-digit code from your app:</p>
                <div className="flex gap-2">
                  <Input value={code} onChange={e => setCode(e.target.value)} data-testid="2fa-verify-code"
                    className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-center text-xl tracking-[0.3em] font-mono w-48"
                    placeholder="000000" maxLength={6} />
                  <Button onClick={handleConfirm2FA} disabled={loading} data-testid="confirm-2fa-btn"
                    className="bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
                    {loading ? 'Verifying...' : 'Verify & Enable'}
                  </Button>
                </div>
              </div>
              <button onClick={() => { setSetupStep(null); setCode(''); }} className="text-sm text-[#808080] hover:text-white">
                Cancel
              </button>
            </div>
          )}

          {user?.totp_enabled && (
            <div className="space-y-3 mt-4 pt-4 border-t border-[rgba(255,255,255,0.06)]">
              <p className="text-sm text-[#B0B0B0]">To disable 2FA, enter your current code:</p>
              <div className="flex gap-2">
                <Input value={disableCode} onChange={e => setDisableCode(e.target.value)} data-testid="disable-2fa-code"
                  className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-center text-xl tracking-[0.3em] font-mono w-48"
                  placeholder="000000" maxLength={6} />
                <Button onClick={handleDisable2FA} disabled={loading} variant="outline"
                  className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10">
                  Disable 2FA
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* About */}
        <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-6">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <SettingsIcon size={18} className="text-[#00FF99]" /> About
          </h2>
          <p className="text-sm text-[#B0B0B0]">
            <span className="text-[#00FF99] font-bold">LCBSE CRM</span> — Life Can Be So Easy
          </p>
          <p className="text-xs text-[#808080] mt-1">
            Telecom &middot; Insurance &middot; Real Estate
          </p>
        </div>
      </div>
    </div>
  );
}
